import { ARDOQ_DEFAULT_LOCALE } from './locales';
import { currencies } from './currencies';
import { FormatOptionValue, Locale } from './types';

const numberFormatCache: Record<string, Intl.NumberFormat> = {};

// currency code list: http://www.currency-iso.org/en/home/tables/table-a1.html
const mapCurrencyToNumberFormatOption = (): [string, FormatOptionValue][] =>
  currencies.map(({ code, label }) => [
    `currency${code}`,
    {
      val: `currency${code}`,
      option: { style: 'currency', currency: code },
      abbreviatedOption: {
        style: 'currency',
        currency: code,
        notation: 'compact',
        maximumFractionDigits: 0,
      },
      label,
      category: 'currency',
    },
  ]);

export const numberFormatOptions: Map<string, FormatOptionValue> = new Map([
  [
    'default',
    {
      val: 'default',
      option: {},
      abbreviatedOption: { notation: 'compact', maximumFractionDigits: 0 },
      label: 'Default',
      category: '',
    },
  ],
  [
    'decimal',
    {
      val: 'decimal',
      option: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      abbreviatedOption: { notation: 'compact', maximumFractionDigits: 0 },
      label: 'Decimal',
      category: '',
    },
  ],
  [
    'percentage',
    {
      val: 'percentage',
      option: {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      },
      abbreviatedOption: {
        style: 'percent',
        notation: 'compact',
        maximumFractionDigits: 0,
      },
      label: '% - Percentage',
      category: 'percentage',
    },
  ],
  ...mapCurrencyToNumberFormatOption(),
]);

export const getNumberFormatIdentifiers = () => {
  return Array.from(numberFormatOptions.keys());
};

export const getNumberFormat = (
  formatOption: Intl.NumberFormatOptions,
  locale = ARDOQ_DEFAULT_LOCALE
) => {
  const key = JSON.stringify([formatOption, locale.code]);

  if (!numberFormatCache[key]) {
    numberFormatCache[key] = new Intl.NumberFormat(locale.code, formatOption);
  }

  return numberFormatCache[key];
};

export const formatNumber = (
  number: number | null | undefined,
  option = 'default',
  locale = ARDOQ_DEFAULT_LOCALE,
  isAbbreviated = false
) => {
  if (number === undefined || number === null || isNaN(number)) {
    return '';
  }

  const optionName = isAbbreviated ? 'abbreviatedOption' : 'option';

  const numberFormatOption =
    numberFormatOptions.get(option)?.[optionName] ?? {};

  return getNumberFormat(numberFormatOption, locale).format(number);
};

export const formatToFixed = (
  currentLocale: Locale,
  number: number,
  numberOfDecimals = 2
) =>
  getNumberFormat(
    { maximumFractionDigits: numberOfDecimals },
    currentLocale
  ).format(number);

export const isCurrencyFormat = (option?: string): option is string =>
  option
    ? Boolean(numberFormatOptions.get(option)?.option.style === 'currency')
    : false;

export const isPercentageFormat = (option?: string): option is 'percentage' =>
  option
    ? Boolean(numberFormatOptions.get(option)?.option.style === 'percent')
    : false;

export const getCurrencySymbol = (option: string, locale: Locale) => {
  const formatOption = numberFormatOptions.get(option)?.option;

  return formatOption
    ? getNumberFormat(formatOption, locale)
        .formatToParts(1.1)
        .find(({ type }) => type === 'currency')?.value
    : '';
};

export const isCommaDecimalSeparator = (locale: Locale) =>
  getNumberFormat({}, locale)
    .formatToParts(1.1)
    .find(({ type }) => type === 'decimal')!.value === ',';

/**
 * Converts a stored percentage value (fraction) to an edit value (percentage).
 * For example: 0.25 -> 25
 * Includes rounding to avoid floating-point precision issues.
 */
export const percentageStoredValueToEditValue = (value: number): number =>
  Math.round(value * 100 * 1e10) / 1e10;

/**
 * Converts an edit percentage value to a stored value (fraction).
 * For example: 25 -> 0.25
 */
export const percentageEditValueToStoredValue = (value: number): number =>
  value / 100;
